import {
  BOOLEAN_OPERATORS,
  DATE_OPERATORS,
  ENUM_OPERATORS,
  NUMBER_OPERATORS,
  QueryFieldDefinition,
  TEXT_OPERATORS,
} from './types'

/**
 * Queryable fields for the TASK module.
 *
 * Custom fields are additionally queryable via the `cf.` prefix, e.g.
 * `cf.budget > 5000` — those are resolved dynamically against the org's
 * CustomFieldDefinition table and are not listed here.
 */
export const TASK_QUERY_FIELDS: QueryFieldDefinition[] = [
  {
    key: 'text',
    label: 'Text',
    type: 'text',
    description: 'Searches name, description and task number',
    example: 'text ~ "payment bug"',
    operators: ['~', '!~'],
  },
  {
    key: 'name',
    aliases: ['summary', 'title'],
    label: 'Name',
    type: 'text',
    example: 'name ~ "onboarding"',
    operators: TEXT_OPERATORS,
    sortable: true,
  },
  {
    key: 'description',
    label: 'Description',
    type: 'text',
    operators: ['~', '!~', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'key',
    aliases: ['taskNumber', 'number'],
    label: 'Task number',
    type: 'text',
    example: 'key = YOLK-134',
    operators: ['=', '!=', '~', 'IN', 'NOT IN'],
  },
  {
    key: 'status',
    label: 'Status',
    type: 'enum',
    description: 'Matches status name or id (e.g. "In Progress", todo)',
    example: 'status = "In Progress"',
    operators: ENUM_OPERATORS,
    optionSource: 'statuses',
    sortable: true,
  },
  {
    key: 'statusType',
    aliases: ['statusCategory'],
    label: 'Status type',
    type: 'enum',
    description: 'Semantic status category',
    example: 'statusType != COMPLETED',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'NOT_STARTED', label: 'Not Started' },
      { value: 'IN_PROGRESS', label: 'In Progress' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'ON_HOLD', label: 'On Hold' },
      { value: 'CANCELED', label: 'Canceled' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'enum',
    example: 'priority IN (HIGH, URGENT)',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'LOW', label: 'Low' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'HIGH', label: 'High' },
      { value: 'URGENT', label: 'Urgent' },
    ],
    sortable: true,
  },
  {
    key: 'assignee',
    label: 'Assignee',
    type: 'user',
    description: 'Matches by name or email; currentUser() supported',
    example: 'assignee = currentUser()',
    operators: [
      '=',
      '!=',
      'IN',
      'NOT IN',
      'IS EMPTY',
      'IS NOT EMPTY',
    ],
    optionSource: 'members',
    supportsCurrentUser: true,
  },
  {
    key: 'creator',
    aliases: ['reporter', 'createdBy'],
    label: 'Creator',
    type: 'user',
    example: 'creator = currentUser()',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
    optionSource: 'members',
    supportsCurrentUser: true,
  },
  {
    key: 'project',
    label: 'Project',
    type: 'reference',
    description: 'Matches project name or id',
    example: 'project = "Website Redesign"',
    operators: ENUM_OPERATORS,
    optionSource: 'projects',
    sortable: true,
  },
  {
    key: 'sprint',
    label: 'Sprint',
    type: 'reference',
    example: 'sprint = "Sprint 12"',
    operators: ENUM_OPERATORS,
    optionSource: 'sprints',
  },
  {
    key: 'group',
    aliases: ['taskGroup'],
    label: 'Task group',
    type: 'reference',
    operators: ENUM_OPERATORS,
    optionSource: 'groups',
  },
  {
    key: 'type',
    aliases: ['taskType'],
    label: 'Task type',
    type: 'reference',
    description: 'Matches task type key or name',
    example: 'type = bug',
    operators: ENUM_OPERATORS,
    optionSource: 'taskTypes',
  },
  {
    key: 'parent',
    aliases: ['parentTask'],
    label: 'Parent task',
    type: 'reference',
    description: 'Matches parent task number; IS EMPTY = top-level tasks',
    example: 'parent = YOLK-12',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'due',
    aliases: ['dueDate'],
    label: 'Due date',
    type: 'date',
    example: 'due <= endOfWeek()',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'scheduled',
    aliases: ['scheduledFor'],
    label: 'Scheduled date',
    type: 'date',
    example: 'scheduled >= startOfWeek()',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'created',
    aliases: ['createdAt'],
    label: 'Created date',
    type: 'date',
    example: 'created >= -30d',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'updated',
    aliases: ['updatedAt'],
    label: 'Updated date',
    type: 'date',
    example: 'updated >= -7d',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'completed',
    aliases: ['completedAt'],
    label: 'Completed date',
    type: 'date',
    example: 'completed >= startOfMonth()',
    operators: DATE_OPERATORS,
  },
  {
    key: 'storyPoints',
    aliases: ['points'],
    label: 'Story points',
    type: 'number',
    example: 'storyPoints >= 5',
    operators: NUMBER_OPERATORS,
  },
  {
    key: 'estimatedHours',
    aliases: ['estimate'],
    label: 'Estimated hours',
    type: 'number',
    operators: NUMBER_OPERATORS,
  },
  {
    key: 'isPrivate',
    aliases: ['private'],
    label: 'Is private',
    type: 'boolean',
    example: 'isPrivate = false',
    operators: BOOLEAN_OPERATORS,
  },
  {
    key: 'isArchived',
    aliases: ['archived'],
    label: 'Is archived',
    type: 'boolean',
    operators: BOOLEAN_OPERATORS,
  },
  {
    key: 'isRecurring',
    aliases: ['recurring'],
    label: 'Is recurring',
    type: 'boolean',
    operators: BOOLEAN_OPERATORS,
  },
  {
    key: 'slaBreached',
    label: 'SLA breached',
    type: 'boolean',
    example: 'slaBreached = true',
    operators: BOOLEAN_OPERATORS,
  },
]

/** Look up a task query field by key or alias (case-insensitive). */
export function findTaskQueryField(
  name: string,
): QueryFieldDefinition | undefined {
  const lower = name.toLowerCase()
  return TASK_QUERY_FIELDS.find(
    field =>
      field.key.toLowerCase() === lower ||
      field.aliases?.some(alias => alias.toLowerCase() === lower),
  )
}

/** Custom field references use the `cf.` prefix: cf.<fieldName> */
export const CUSTOM_FIELD_QUERY_PREFIX = 'cf.'

export function isCustomFieldQueryKey(name: string): boolean {
  return name.toLowerCase().startsWith(CUSTOM_FIELD_QUERY_PREFIX)
}
